import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarIcon } from './icons/CalendarIcon'
import { POPOVER_OPEN_EVENT } from '../lib/popoverBus'

export interface DateRangePeriod {
  label: string
  days: number
}

export interface DateRangeValue {
  start: string | null
  end: string | null
  periodLabel: string | null
}

export interface DateRangeFilterProps {
  periods: DateRangePeriod[]
  value: DateRangeValue
  onChange: (next: DateRangeValue) => void
  ariaLabel: string
}

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function periodToRange(days: number): { start: string; end: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - days + 1)
  return { start: toDateInputValue(start), end: toDateInputValue(end) }
}

export function DateRangeFilter({ periods, value, onChange, ariaLabel }: DateRangeFilterProps) {
  const { t } = useTranslation()
  const instanceId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<DateRangeValue>(value)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen])

  useEffect(() => {
    function handleOtherPopoverOpen(e: Event) {
      const openedId = (e as CustomEvent<string>).detail
      if (openedId !== instanceId) setIsOpen(false)
    }
    window.addEventListener(POPOVER_OPEN_EVENT, handleOtherPopoverOpen)
    return () => window.removeEventListener(POPOVER_OPEN_EVENT, handleOtherPopoverOpen)
  }, [instanceId])

  function toggleOpen() {
    setIsOpen(prev => {
      const next = !prev
      if (next) {
        window.dispatchEvent(new CustomEvent(POPOVER_OPEN_EVENT, { detail: instanceId }))
        // When a named period is active but no dates were passed in, compute them so
        // the START/END inputs show actual values rather than empty placeholders.
        if (value.periodLabel && !value.start && !value.end) {
          const match = periods.find(p => p.label === value.periodLabel)
          if (match) {
            const { start, end } = periodToRange(match.days)
            setDraft({ start, end, periodLabel: value.periodLabel })
            return next
          }
        }
        setDraft(value)
      }
      return next
    })
  }

  function pickPeriod(period: DateRangePeriod) {
    const { start, end } = periodToRange(period.days)
    setDraft({ start, end, periodLabel: period.label })
  }

  function handleManualChange(field: 'start' | 'end', v: string) {
    setDraft(prev => ({ ...prev, [field]: v, periodLabel: null }))
  }

  const dateInvalid =
    draft.periodLabel === null && !!draft.start && !!draft.end && draft.start > draft.end

  function apply() {
    if (dateInvalid) return
    onChange(draft)
    setIsOpen(false)
  }

  function cancel() {
    setIsOpen(false)
  }

  const triggerLabel =
    value.periodLabel ??
    (value.start && value.end ? `${value.start} – ${value.end}` : t('auditLog.filters.selectPeriod'))

  return (
    <div ref={containerRef} className="daterange">
      <button
        type="button"
        className="daterange-trigger"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={toggleOpen}
      >
        <span className="dr-label">{triggerLabel}</span>
        <CalendarIcon />
      </button>
      {isOpen && (
        <div className="daterange-pop open">
          <div className="dr-dates">
            <div className="filter-field">
              <label>{t('auditLog.filters.start')}</label>
              <input
                type="date"
                className="form-input dr-start"
                value={draft.start ?? ''}
                onChange={e => handleManualChange('start', e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>{t('auditLog.filters.end')}</label>
              <input
                type="date"
                className="form-input dr-end"
                value={draft.end ?? ''}
                onChange={e => handleManualChange('end', e.target.value)}
              />
            </div>
          </div>
          <div className="dr-periods">
            {periods.map(p => (
              <button
                key={p.label}
                type="button"
                className={`dr-period-btn${draft.periodLabel === p.label ? ' active' : ''}`}
                onClick={() => pickPeriod(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {dateInvalid && (
            <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--danger)', lineHeight: 1.4 }}>
              {t('auditLog.filters.dateRangeInvalid')}
            </p>
          )}
          <div className="dr-apply">
            <button
              type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}
              onClick={cancel}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px' }}
              onClick={apply}
              disabled={dateInvalid}
            >
              {t('common.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
